import { COLORS } from '@r1-frontend/ui-react/assets/js/constants';
import { MDASH, NBSP } from '@r1-frontend/ui-react/tokens/symbols';

import { DEFAULT_URL, EPlusesCategory, STATION_IMG_URL } from '~/src/features/yandex-landing/constants';
import type { TBenefits, TCarouselStation, TGraphic, TPriceData } from '~/src/features/yandex-landing/data/types';
import { getDataOfOneDiffParams } from '~/src/features/yandex-landing/utils';

/**
 * @name data.globalId
 * @type {number}
 */
let globalId = 0;

/**
 * Данные блока баннер в хедере
 *
 * @name data.benefits
 * @type {TBenefits[]}
 */
export const benefits: TBenefits[] = [
    { imgUrl: `${DEFAULT_URL}yandex-station-icon.svg`, text: 'Яндекс.Станция Лайт', id: globalId++ },
    { imgUrl: `${DEFAULT_URL}subscribe.svg`, text: 'Подписка Яндекс Плюс на 1 месяц', id: globalId++ },
    { imgUrl: `${DEFAULT_URL}delivery.svg`, text: 'Бесплатная доставка и установка', id: globalId++ },
];

/**
 * Данные блока c ценой в хедере
 *
 * @name data.priceData
 * @type {TPriceData[]}
 */
export const priceData: TPriceData[] = [
    { price: '', title: 'Цена Дом.ру', id: globalId++ },
];

/**
 * Данные для карусели в хедере
 *
 * @name data.headerCarouselRowData
 * @type {TBenefits[]}
 */
export const headerCarouselRowData: TBenefits[] = [
    { imgUrl: `${STATION_IMG_URL}max.png`, imgWebpUrl: `${STATION_IMG_URL}max.webp`, text: 'Яндекс Станция Макс с Zigbee', id: globalId++, type: 'maxi' },
    { imgUrl: `${STATION_IMG_URL}default.png`, imgWebpUrl: `${STATION_IMG_URL}default.webp`, text: 'Яндекс Станция Стандарт 2', id: globalId++, type: 'standart' },
    { imgUrl: `${STATION_IMG_URL}midi.png`, imgWebpUrl: `${STATION_IMG_URL}midi.webp`, text: 'Яндекс Станция Миди', id: globalId++, type: 'midi' },
    { imgUrl: `${STATION_IMG_URL}mini.png`, imgWebpUrl: `${STATION_IMG_URL}mini.webp`, text: 'Яндекс Станция Мини с часами', id: globalId++, type: 'mini' },
    { imgUrl: `${STATION_IMG_URL}light.png`, imgWebpUrl: `${STATION_IMG_URL}light.webp`, text: 'Яндекс Станция Лайт', id: globalId++, type: 'light' },
];

/**
 * Данные для графических блоков в разделе "Плюсы"
 *
 * @name data.graphicData
 * @type {TGraphic[]}
 */
export const graphicData: TGraphic[] = [
    {
        area: EPlusesCategory.musicAndMovie,
        id: globalId++,
        gradient: '327.54deg, #eea64d 5.62%, #ac4ebb 38.28%, #505ed5 68.49%',
        label: {
            desktop: `${DEFAULT_URL}Ylabel-desktop.png`,
            mobile: `${DEFAULT_URL}Ylabel-desktop.png`,
        },
        imgUrl: `${DEFAULT_URL}services.svg`,
        initialTypeIsRow: true,
        title: `Кино${NBSP}и музыка \nв одной подписке`,
        subText: `Слушайте персональные плейлисты на${NBSP}Яндекс.Музыке, выбирайте что \nпосмотреть` +
            ` из${NBSP}огромного каталога Кинопоиска и получайте баллы Плюса. `,
    },
    {
        area: EPlusesCategory.freeDelivery,
        id: globalId++,
        gradient: '132.21deg, #FFF8D2 -1.29%, #F8DBB9 97.33%',
        imgUrl: `${DEFAULT_URL}delivery-station.png`,
        title: 'Бесплатная доставка\nи установка',
        subText: `Привезем к${NBSP}вам домой в${NBSP}удобное время`,
    },
    {
        area: EPlusesCategory.installment,
        id: globalId++,
        layoutImg: `${DEFAULT_URL}installment-layout.png`,
        imgUrl: `${DEFAULT_URL}installment.png`,
        title: 'Есть, что выбрать',
        subText: `Разнообразие моделей и${NBSP}расцветок${NBSP}${MDASH} под любой интерьер и${NBSP}задачи`,
        hasImgStyle: {
            tablet: true,
            mobile: true,
        },
    },
    {
        area: EPlusesCategory.useNow,
        id: globalId++,
        gradient: '180deg, #ffde52 0, #ffe46a 100%',
        imgUrl: `${DEFAULT_URL}now.png`,
        title: 'Пользуйтесь сразу',
        subText: `Все настроим, покажем и${NBSP}ответим на${NBSP}вопросы`,
        hasImgStyle: {
            tablet: true,
        },
    },
];

/**
 * Общие пропсы слайдеров
 *
 * @name data.commonPropsSlider
 * @type {Object}
 */
export const commonPropsSlider = {
    showArrows: false,
    infinite: true,
    align: 'center',
};

/**
 * Пропсы для Slider в секции каталог
 *
 * @name data.sliderPropsCatalog
 * @type {Object}
 */
export const sliderPropsCatalog = {
    slidesToShow: 3,
    wrapAround: true,
    withoutArrows: true,
};

/**
 * Пропсы для Slider в секции умная-колонка
 *
 * @name data.sliderPropsSmartStation
 * @type {Object}
 */
export const sliderPropsSmartStation = {
    slidesToShow: 1,
    autoplay: true,
    autoplayDelay: 1000,
    ...commonPropsSlider,
};

/**
 * Данные для карусели в разделе "Умная колонка"
 *
 * @name data.CarouselStationData
 * @type {TCarouselStation}
 */
export const CarouselStationData: TCarouselStation = {
    titleMain: ['Показывает кино', 'Ставит музыку', 'Развлекает детей', 'Управляет приборами', 'Помогает по дому'],
    subTextMain: [
        'Достаточно подключить колонку \nк телевизору или проектору',
        'Вы только попросите',
        'А дети её за это любят',
        'Но только умными и только \nс вашего позволения',
        'И никогда не устаёт',
    ],
    imgUrlBaseMain: {
        url: `${DEFAULT_URL}carousel-img-`,
        type: '.jpg',
    },
    iconUrlBase: {
        iconUrl: `${DEFAULT_URL}slips-icons/icon-`,
        typeIcon: '.svg',
    },
    slipText: [
        ['Весь КиноПоиск в \nодной колонке', 'Голос вместо пульта', 'Объёмный звук \nкак в кино'],
        ['Миллионы треков \nи подкастов', 'Все российские \nрадиостанции', 'Успокаивающие \nзвуки природы'],
        ['Расскажет \nна ночь сказку', 'Научит новому \nи полезному', 'Устроит квест \nили поиграет'],
        ['Выключит свет, \nесли вы забыли', 'Вскипятит воду \nв чайнике', 'Запустит стирку \nили посудомойку'],
        ['Поставит таймер \nили будильник', 'Расскажет \nпрогноз погоды', 'Подскажет, что \nи как приготовить'],
    ],
    backgroundColor: ['#eeeef0', '#02474f', '#7f2200', '#1a103a', '#986f70'],
    positionText: getDataOfOneDiffParams({ soleParam: '0', otherParam: '-270px' }),
    textColor: getDataOfOneDiffParams({ soleParam: COLORS.black, otherParam: COLORS.white }),
};
